import dateFnsTz from 'date-fns-tz';
import { DATE_TIME_LOCALE } from './constants.js';

// Must import from date-fns-tz this way to avoid issues with the prober, which
// also uses the `core` package. See
// https://github.com/marnusw/date-fns-tz/issues/133#issuecomment-929778119
// An alternative option of importing from date-fns-tz/esm was decided against
// because it broke a bunch of other downstream things (e.g. jest tests).
const { getTimezoneOffset } = dateFnsTz;
/**
 * Normalizes some inconsistencies in our allowed internal time zone values to ensure they will
 * work properly when using date-related APIs. Will return default of "local" if time zone
 * not specified.
 */ function normalizeTimeZone(timeZone = 'local') {
    return timeZone === 'utc' ? 'UTC' : timeZone;
}
/**
 * Normalizes our allowed internal time zone values into an IANA time zone value, which is necessary
 * for some time zone formatting utilities.
 */ function normalizeIANATimeZone(timeZone = 'local') {
    const normalizedTimeZone = normalizeTimeZone(timeZone);
    if (isLocalTimeZone(normalizedTimeZone)) {
        return getLocalIANATimeZone();
    }
    return normalizedTimeZone;
}
/**
 * Returns true if the specified time zone value indicates it is using the user's
 * local, browser-based time zone. If not specified, the time zone is assumed to be
 * local.
 */ function isLocalTimeZone(timeZone) {
    const lowerTimeZone = normalizeTimeZone(timeZone).toLowerCase();
    return lowerTimeZone === 'local' || lowerTimeZone === 'browser';
}
/**
 * Get the local browser time zone in IANA format (e.g. America/New_York).
 */ function getLocalIANATimeZone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
/**
 * Returns the time zone offset from UTC in milliseconds.
 */ function getTimeZoneOffsetMs(timeZone) {
    const normalizedTimeZone = normalizeIANATimeZone(timeZone);
    const now = new Date();
    return getTimezoneOffset(normalizedTimeZone, now);
}
/**
 * Returns the name of the time zone.
 */ function getTimeZoneName(timeZone, { variant = 'short' } = {}) {
    const normalizedTimeZone = normalizeIANATimeZone(timeZone);
    const parts = Intl.DateTimeFormat(DATE_TIME_LOCALE, {
        timeZone: normalizedTimeZone,
        timeZoneName: variant
    }).formatToParts(new Date());
    return parts.find((part)=>part.type === 'timeZoneName')?.value;
}

export { getLocalIANATimeZone, getTimeZoneName, getTimeZoneOffsetMs, isLocalTimeZone, normalizeIANATimeZone, normalizeTimeZone };
//# sourceMappingURL=time-zone-utils.js.map
